import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from "recharts";
import { subDays, format, eachDayOfInterval } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { CampaignDetailsDialog } from "@/components/campaigns/CampaignDetailsDialog";
import { ArrowLeft, Play, Pause, Trash2, RefreshCw, TrendingUp, BarChart3, Eye, Clock, Calendar, CalendarClock, Users, Target, AlertTriangle, MessageSquare, MoreHorizontal, Link2, Search, Settings, CheckCircle2, XCircle, Minus, Pencil, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { startCampaign, pauseCampaign, resumeCampaign, deleteCampaign, duplicateCampaign } from "@/lib/campaignHelpers";
import { useCapacityBlock } from "@/components/campaigns/useCapacityBlock";
import { WeeklyLimitBanner } from "@/components/campaigns/WeeklyLimitBanner";
import { calculateCampaignProgress } from "@/lib/campaignProgress";
import {
  extractVariablesFromTemplates,
  getCustomFieldsFromTargets,
  validateCSVForCampaign,
  downloadCSVTemplate,
  CSVValidationResult
} from "@/lib/csvValidation";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useState, useEffect, useMemo, memo } from "react";
import { TargetDetailDialog } from "@/components/campaigns/TargetDetailDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { AiRecommendations } from "./AiRecommendations";
import { CampaignKnowledgeLinker } from "@/components/campaigns/CampaignKnowledgeLinker";
import { ResultsIntentCard } from "./ResultsIntentCard";
import { EditScheduleDialog } from "./EditScheduleDialog";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function formatWorkingDays(days: number[] | null | undefined): string {
  const list = days && days.length ? days : [1, 2, 3, 4, 5];
  if (list.length === 7) return "Every day";
  const sorted = [...list].sort((a, b) => a - b);
  if (sorted.join(",") === "1,2,3,4,5") return "Mon – Fri";
  return sorted.map((d) => DAY_LABELS[d]).join(", ");
}

export default function SimplifiedCampaignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo'); // For navigation back to origin page
  const queryClient = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const saveName = async () => {
    const next = nameDraft.trim();
    if (!next) { toast.error("Campaign name can't be empty"); return; }
    if (!id) return;
    setSavingName(true);
    const { error } = await supabase.from("campaigns").update({ name: next }).eq("id", id);
    setSavingName(false);
    if (error) { toast.error("Couldn't rename campaign"); return; }
    setEditingName(false);
    queryClient.setQueryData(["campaign", id], (old: any) => (old ? { ...old, name: next } : old));
    queryClient.invalidateQueries({ queryKey: ["campaign", id] });
    queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    toast.success("Campaign renamed");
  };
  const { isMember, workspace } = useWorkspace();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [csvValidationResult, setCsvValidationResult] = useState<CSVValidationResult | null>(null);
  const [pendingTargetsToAdd, setPendingTargetsToAdd] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);

  // URL-backed UI state — survives navigation away/back and tab switches.
  const timeRange = searchParams.get('range') || 'month';
  const setTimeRange = (range: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('range', range);
    setSearchParams(next, { replace: true });
  };
  const activeDetailTab = (searchParams.get('tab') === 'details' ? 'details' : 'analytics') as "analytics" | "details";
  const setActiveDetailTab = (tab: "analytics" | "details") => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  // Missing state variables for CSV upload flow
  const [showCSVUploader, setShowCSVUploader] = useState(false);
  const [uploadedTargets, setUploadedTargets] = useState<any[]>([]);
  const [uploadMode, setUploadMode] = useState<'select' | 'upload'>('select');
  // AI Knowledge Linker state
  const [knowledgeLinkerOpen, setKnowledgeLinkerOpen] = useState(false);

  // Check if AI assistant is enabled for workspace
  const isAiEnabled = true;

  const { data: campaign, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select(`
          *,
          clients (
            id,
            email,
            status,
            owner_user_id
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
    // Poll every 2 seconds while campaign is in transitional state (starting/resuming)
    // This ensures UI updates when background job completes
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return (status === 'starting' || status === 'resuming') ? 2000 : false;
    },
  });

  // Fetch member's client to check ownership
  const { data: myClient } = useQuery({
    queryKey: ['my-client', workspace?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data } = await supabase
        .from('clients')
        .select('id')
        .eq('owner_user_id', user.id)
        .eq('workspace_id', workspace?.id)
        .maybeSingle();
      return data;
    },
    enabled: isMember && !!workspace?.id,
  });


  const isArchived = campaign?.status === 'archived';

  // Check if member can edit this campaign
  const canEditCampaign = (!isMember || (myClient?.id && campaign?.client_id === myClient.id)) && !isArchived;
  const isManagedByAdmin = (workspace as any)?.campaigns_managed_by_admin || false;
  const canEditSchedule = campaign?.status === "paused" || campaign?.status === "draft";

  const { data: stats } = useQuery({
    queryKey: ["campaign-stats", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaign_stats_aggregate")
        .select("*")
        .eq("campaign_id", id)
        .single();

      if (error) throw error;

      return {
        totalTargets: Number(data.total_targets) || 0,
        connectionAccepted: Number(data.connection_accepted_count) || 0,
        replied: Number(data.replied_count) || 0,
        messaged: Number(data.messaged_count) || 0,
        pending: Number(data.pending_count) || 0,
        visited: Number(data.visited_count) || 0,
        failed: Number(data.failed_count) || 0,
        connectionSent: Number(data.connection_sent_count) || 0,
      };
    },
    enabled: !!id,
  });

  const { data: targets } = useQuery({
    queryKey: ["campaign-targets", id, (campaign as any)?.execution_list_enabled],
    queryFn: async () => {
      // PostgREST caps any single read at 1000 rows, and campaigns here run
      // well past that — an unpaged read would silently drop the tail and
      // under-report every count on this screen.
      const PAGE = 1000;
      const all: any[] = [];

      for (let from = 0; ; from += PAGE) {
        let query = supabase
          .from("targets")
          .select("*")
          .eq("campaign_id", id);

        // Two-list campaigns: hide the untouched import backlog, but never hide
        // people we already reached out to — their history stays visible.
        if ((campaign as any)?.execution_list_enabled === true) {
          query = query.or(
            "list_stage.eq.execution,connection_sent_at.not.is.null,status.not.in.(pending,visited)"
          );
        }

        const { data, error } = await query
          .order("created_at", { ascending: false })
          .order("id", { ascending: true })
          .range(from, from + PAGE - 1);

        if (error) throw error;
        all.push(...(data || []));
        if (!data || data.length < PAGE) break;
      }

      return all;
    },
    enabled: !!id,
  });


  // Fetch sequences for variable extraction
  const { data: sequences } = useQuery({
    queryKey: ["campaign-sequences", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sequences")
        .select("*")
        .eq("campaign_id", id)
        .order("step_number", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (!id) return;

    // Debounce burst invalidations from action_log inserts during an active
    // campaign (can fire every few seconds). Use refetchType: 'none' so the
    // cache is just marked stale and only refetched on the next user visit
    // — combined with the 30s global staleTime this eliminates background
    // chart refetches while keeping the data fresh on demand.
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleInvalidate = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['campaign-stats', id], refetchType: 'none' });
        queryClient.invalidateQueries({ queryKey: ['campaign-progress', id], refetchType: 'none' });
      }, 5000);
    };

    const channel = supabase
      .channel(`campaign-${id}-actions`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'action_log',
          filter: `campaign_id=eq.${id}`
        },
        () => scheduleInvalidate()
      )
      .subscribe();

    // When the user returns to the tab after >5s away, force one immediate
    // refetch (bypassing the 5s debounce) so they see fresh data on return
    // instead of stale cache.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
        queryClient.invalidateQueries({ queryKey: ['campaign-stats', id] });
        queryClient.invalidateQueries({ queryKey: ['campaign-progress', id] });
        queryClient.invalidateQueries({ queryKey: ['campaign-analytics-daily', id] });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      channel.unsubscribe();
    };
  }, [id, queryClient]);

  const { handleCapacityResult, capacityDialog } = useCapacityBlock();

  const startCampaignMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No campaign ID");
      const result = await startCampaign(id);
      if (!result.success) {
        handleCapacityResult(result, id);
        throw new Error(result.error || "Failed to start campaign");
      }

      return result;
    },
    onSuccess: () => {
      toast.success('Campaign started!', {
        description: 'Actions are being scheduled in the background. Check the Job Queue for progress.',
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-stats", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-progress", id] });
    },
    onError: (error: any) => {
      toast.error("Failed to start campaign: " + error.message);
    },
  });

  const pauseCampaignMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No campaign ID");
      return await pauseCampaign(id);
    },
    onSuccess: () => {
      toast.success("Campaign paused successfully");
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
    },
    onError: (error: any) => {
      toast.error("Failed to pause campaign: " + error.message);
    },
  });

  const resumeCampaignMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No campaign ID");
      const result = await resumeCampaign(id);
      if (!result.success) {
        handleCapacityResult(result, id);
        throw new Error(result.error || "Failed to resume campaign");
      }

      return result;
    },
    onSuccess: () => {
      toast.success('Campaign resumed!', {
        description: 'Actions are being scheduled in the background. Check the Job Queue for progress.',
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-stats", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-progress", id] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to resume campaign: ${error.message}`);
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No campaign ID");
      return await deleteCampaign(id);
    },
    onSuccess: () => {
      toast.success("Campaign deleted successfully");
      navigate(returnTo || "/campaigns");
    },
    onError: (error: any) => {
      toast.error("Failed to delete campaign: " + error.message);
    },
  });

  const deleteTargetMutation = useMutation({
    mutationFn: async (targetId: string) => {
      const { error } = await supabase
        .from("targets")
        .delete()
        .eq("id", targetId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Target deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["campaign-targets", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-stats", id] });
      setDeleteTargetId(null);
    },
    onError: (error: any) => {
      toast.error("Failed to delete target: " + error.message);
    },
  });

  const duplicateCampaignMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No campaign ID");
      const result = await duplicateCampaign(id);
      if (!result.success) {
        throw new Error(result.error || "Failed to duplicate campaign");
      }
      return result;
    },
    onSuccess: (data) => {
      toast.success("Campaign duplicated successfully");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      if (data.newCampaignId) {
        navigate(`/campaigns/${data.newCampaignId}`);
      }
    },
    onError: (error: any) => {
      toast.error("Failed to duplicate campaign: " + error.message);
    },
  });

  // Mutation to add targets from CSV upload
  const addTargetsMutation = useMutation({
    mutationFn: async (targets: any[]) => {
      if (!id) throw new Error("No campaign ID");

      // Process targets in batches
      const BATCH_SIZE = 500;
      for (let i = 0; i < targets.length; i += BATCH_SIZE) {
        const batch = targets.slice(i, i + BATCH_SIZE).map(target => ({
          campaign_id: id,
          profile_url: target.profile_url,
          first_name: target.first_name,
          last_name: target.last_name,
          name: target.name,
          title: target.title,
          company: target.company,
          custom_data: target.custom_data,
          sales_nav_url: target.sales_nav_url,
          link_type: target.link_type,
          linkedin_profile_id: target.provider_id,
          status: 'pending',
          current_sequence_step: 0,
        }));

        const { error } = await supabase
          .from('targets')
          .insert(batch);

        if (error) throw error;
      }
      return { count: targets.length };
    },
    onSuccess: (data) => {
      toast.success(`Successfully added ${data.count} targets to campaign`);
      setShowCSVUploader(false);
      setUploadedTargets([]);
      queryClient.invalidateQueries({ queryKey: ["campaign-targets", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-stats", id] });
    },
    onError: (error: any) => {
      toast.error("Failed to add targets: " + error.message);
    },
  });

  const handleCSVUpload = async (newTargets: any[], headers: string[]) => {
    if (!id || newTargets.length === 0) return;

    setUploadedTargets(newTargets);
    setCsvHeaders(headers);

    // Validate CSV against campaign requirements
    const validation = await validateCSVForCampaign(
      id,
      headers,
      newTargets,
      sequences || [],
      targets || []
    );

    // Filter out duplicates from targets
    const duplicateUrlsSet = new Set(validation.duplicateUrls.map(u => u.toLowerCase().trim().replace(/\/$/, '')));
    const filteredTargets = newTargets.filter(t =>
      !duplicateUrlsSet.has(t.profile_url?.toLowerCase().trim().replace(/\/$/, '') || '')
    );

    // If there are warnings, show confirmation dialog
    if (validation.warnings.length > 0) {
      setCsvValidationResult(validation);
      setPendingTargetsToAdd(filteredTargets);
    } else {
      // No warnings, proceed directly
      if (filteredTargets.length > 0) {
        addTargetsMutation.mutate(filteredTargets);
      } else {
        toast.error("No new targets to add (all duplicates)");
      }
    }
  };

  const handleConfirmAddTargets = () => {
    if (pendingTargetsToAdd.length > 0) {
      addTargetsMutation.mutate(pendingTargetsToAdd);
    }
    setCsvValidationResult(null);
    setPendingTargetsToAdd([]);
    setCsvHeaders([]);
  };

  const handleCancelAddTargets = () => {
    setCsvValidationResult(null);
    setPendingTargetsToAdd([]);
    setCsvHeaders([]);
    setShowCSVUploader(false);
    setUploadMode('select');
  };

  const handleDownloadTemplate = () => {
    if (!campaign || !sequences) return;

    const requiredVariables = extractVariablesFromTemplates(sequences);
    const existingCustomFields = getCustomFieldsFromTargets(targets || []);

    downloadCSVTemplate(campaign.name, requiredVariables, existingCustomFields);
    toast.success("Template downloaded!");
  };

  const handleToggleCampaign = () => {
    if (!campaign) return;

    if (campaign.status === 'active') {
      pauseCampaignMutation.mutate();
    } else if (campaign.status === 'paused') {
      resumeCampaignMutation.mutate();
    } else {
      startCampaignMutation.mutate();
    }
  };


  // Show "campaign not found" only after loading completes.
  if (!isLoading && !campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
        <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <Target className="w-8 h-8 opacity-50" />
        </div>
        <p className="text-lg font-medium mb-2">Campaign not found</p>
        <Button onClick={() => navigate(returnTo || "/campaigns")}>Back to Campaigns</Button>
      </div>
    );
  }

  // Cold-cache initial load: render a stable skeleton scaffold matching the
  // page layout so there is no flash, no spinner, and no layout jump when
  // data arrives. Once cached (TanStack staleTime/gcTime), this is skipped
  // entirely on subsequent visits.
  if (!campaign) {
    return (
      <div className="space-y-6 sm:space-y-8 pb-12 sm:pb-16 px-2 sm:px-0">
        <div className="flex flex-col gap-4 sm:gap-6 border-b border-border pb-4 sm:pb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(returnTo || "/campaigns")}
              className="text-muted-foreground hover:text-foreground -ml-2 text-xs sm:text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </div>
          <div className="space-y-3">
            <div className="h-8 w-64 bg-muted/60 animate-pulse rounded-md" />
            <div className="h-4 w-80 bg-muted/40 animate-pulse rounded-md" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-muted/40 animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-2 h-[340px] bg-muted/40 animate-pulse rounded-lg" />
          <div className="h-[290px] bg-muted/40 animate-pulse rounded-lg" />
          <div className="h-[290px] bg-muted/40 animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  const client = campaign.clients as any;

  return (
    <div className="space-y-6 sm:space-y-8 pb-12 sm:pb-16 animate-in fade-in slide-in-from-bottom-4 duration-500 px-2 sm:px-0">
      {/* Header Section */}
      <div className="flex flex-col gap-4 sm:gap-6 border-b border-border pb-4 sm:pb-6">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(returnTo || "/campaigns")}
            className="text-muted-foreground hover:text-foreground -ml-2 text-xs sm:text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="h-4 w-[1px] bg-border" />
          <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 truncate">
            <Users className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{client?.email || "Unknown Account"}</span>
          </span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6">
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              {editingName ? (
                <div className="flex items-center gap-1.5">
                  <Input autoFocus value={nameDraft} maxLength={120}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                    className="h-10 w-[min(28rem,80vw)] text-lg sm:text-xl font-bold" />
                  <Button size="icon" className="h-9 w-9 shrink-0" onClick={saveName} disabled={savingName} aria-label="Save name">
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => setEditingName(false)} aria-label="Cancel">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="group flex items-center gap-1.5 min-w-0">
                  <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-foreground">{campaign.name}</h1>
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-primary" aria-label="Rename campaign"
                    onClick={() => { setNameDraft(campaign.name || ""); setEditingName(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <StatusBadge status={campaign.status as any} className="text-xs sm:text-sm px-2 sm:px-3 py-0.5 w-fit" />
            </div>
            <p className="text-muted-foreground flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {campaign.timezone || 'UTC'}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Created {new Date(campaign.created_at).toLocaleDateString()}
              </span>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:gap-3 shrink-0">

            {/* Time Range Toggle - Left of Resume Button */}
            <div className="flex bg-muted p-1 rounded-lg">
              <button
                onClick={() => setTimeRange('week')}
                className={cn(
                  "px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium rounded-md transition-all",
                  timeRange === 'week' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Week
              </button>
              <button
                onClick={() => setTimeRange('month')}
                className={cn(
                  "px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium rounded-md transition-all",
                  timeRange === 'month' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Month
              </button>
              <button
                onClick={() => setTimeRange('lifetime')}
                className={cn(
                  "px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium rounded-md transition-all",
                  timeRange === 'lifetime' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Lifetime
              </button>
            </div>

            {/* View Only badge for members who don't own this campaign */}
            {!canEditCampaign && (
              <Badge variant="outline" className="text-muted-foreground text-xs">
                View Only
              </Badge>
            )}

            {/* Awaiting limit assignment — created while the account was fully allocated */}
            {canEditCampaign && campaign.status !== "active" && campaign.status !== "completed" && campaign.status !== "archived"
              && (campaign.max_connections_daily ?? 0) === 0 && (campaign.max_messages_daily ?? 0) === 0 && (
              <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-normal text-xs py-1.5 px-3">
                <Clock className="w-3.5 h-3.5 mr-1.5" />
                Awaiting limit assignment — our team is configuring this campaign
              </Badge>
            )}

            {/* Start/Pause/Resume button - only if member can edit */}
            {canEditCampaign && campaign.status !== "completed" && campaign.status !== "archived" && campaign.status !== "starting" && campaign.status !== "resuming"
              && !(campaign.status !== "active" && (campaign.max_connections_daily ?? 0) === 0 && (campaign.max_messages_daily ?? 0) === 0) && (
              <Button
                variant={campaign.status === "active" ? "secondary" : "default"}
                onClick={handleToggleCampaign}
                disabled={startCampaignMutation.isPending || pauseCampaignMutation.isPending || resumeCampaignMutation.isPending}
                size="sm"
                className={cn(
                  "min-w-[100px] sm:min-w-[140px] shadow-lg transition-all text-xs sm:text-sm",
                  campaign.status === "active" ? "hover:bg-muted" : "hover:shadow-primary/25"
                )}
              >
                {campaign.status === "active" ? (
                  <>
                    <Pause className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Pause
                  </>
                ) : (
                  <>
                    <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> {campaign.status === "paused" ? "Resume" : "Start"}
                  </>
                )}
              </Button>
            )}


            {/* Simplified Workspace: View Target Audience & Edit Sequence - Right of Resume Button */}
            {canEditCampaign && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8 sm:h-10 sm:w-10">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/campaigns/${id}/audience`)}>
                    <Users className="w-4 h-4 mr-2" />
                    View Target Audience
                  </DropdownMenuItem>
                  {campaign.status !== 'archived' && campaign.status !== 'completed' && campaign.status !== 'starting' && campaign.status !== 'resuming' && (
                    <DropdownMenuItem onClick={() => navigate(`/campaigns/${id}/sequence`)}>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Change Messages
                    </DropdownMenuItem>
                  )}
                  {isAiEnabled && (
                    <DropdownMenuItem onClick={() => setKnowledgeLinkerOpen(true)}>
                      <Link2 className="w-4 h-4 mr-2" />
                      Link AI Knowledge
                    </DropdownMenuItem>
                  )}
                    <DropdownMenuItem onClick={() => navigate(returnTo ? `/campaigns/${id}/settings?returnTo=${encodeURIComponent(returnTo)}` : `/campaigns/${id}/settings`)}>
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {canEditCampaign && (campaign.status === "starting" || campaign.status === "resuming") && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  disabled
                  size="sm"
                  className="min-w-[90px] sm:min-w-[120px] animate-pulse text-xs sm:text-sm"
                >
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                  {campaign.status === "starting" ? "Starting..." : "Resuming..."}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="text-xs sm:text-sm"
                  onClick={async () => {
                    const previousStatus = campaign.status === "starting" ? "draft" : "paused";
                    await supabase
                      .from("campaigns")
                      .update({ status: previousStatus })
                      .eq("id", id);
                    queryClient.invalidateQueries({ queryKey: ["campaign", id] });
                    toast.success("Process cancelled", {
                      description: `Campaign reverted to ${previousStatus} status.`
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Explains when a profile pre-fetch campaign will start sending. Renders
          nothing for campaigns without the execution list. */}

      {(campaign?.status === 'active' || campaign?.status === 'paused') && (
        <WeeklyLimitBanner clientId={campaign?.client_id} scopeKey={(campaign as any)?.workspace_id} timezone={(campaign as any)?.timezone} paused={campaign?.status === 'paused'} />
      )}

      {/* Sending schedule — editable by the client only while paused/draft */}
      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <CalendarClock className="w-4 h-4 text-primary" />
              Sending schedule
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
              <span>{formatWorkingDays(campaign.working_days)}</span>
              <span>
                {String(campaign.working_hours_start ?? 9).padStart(2, "0")}:00 – {String(campaign.working_hours_end ?? 18).padStart(2, "0")}:00
              </span>
              <span>{campaign.timezone || "UTC"}</span>
              <span className="text-muted-foreground/70">
                Daily limits: {campaign.max_connections_daily ?? "—"} invites · {campaign.max_messages_daily ?? "—"} messages
              </span>
            </p>
          </div>
          {canEditCampaign && !isManagedByAdmin && (
            canEditSchedule ? (
              <Button variant="outline" size="sm" onClick={() => setScheduleDialogOpen(true)} className="shrink-0">
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                Edit schedule
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground shrink-0">Pause the campaign to change its schedule</span>
            )
          )}
        </CardContent>
      </Card>
      {id && (
        <EditScheduleDialog
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
          campaignId={id}
          campaign={campaign}
        />
      )}


      {/* AI Recommendations Section - Hidden until feature is ready */}
      {/* TODO: Re-enable when AI recommendations feature is production-ready
      {isAiEnabled && stats && (
        <AiRecommendations
          campaignId={id!}
          workspaceId={workspace?.id || ""}
          campaignStats={{
            totalTargets: stats.totalTargets,
            connectionSent: stats.connectionSent,
            connectionAccepted: stats.connectionAccepted,
            messaged: stats.messaged,
            replied: stats.replied
          }}
        />
      )}
      */}

      {/* Simplified Analytics Section with Tabs */}
      <SimplifiedAnalyticsCharts
        campaignId={id!}
        stats={stats}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        targets={targets}
        isDetailedView
        activeTab={activeDetailTab}
        setActiveTab={setActiveDetailTab}
      />





      {/* Dialogs */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this campaign and all associated data including targets and sequences.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCampaignMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTargetId} onOpenChange={(open) => !open && setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Target</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this target? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTargetId && deleteTargetMutation.mutate(deleteTargetId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTargetMutation.isPending}
            >
              {deleteTargetMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CampaignDetailsDialog
        campaignId={id!}
        open={analyticsDialogOpen}
        onOpenChange={setAnalyticsDialogOpen}
        defaultTab="analytics"
      />

      {selectedTargetId && (
        <TargetDetailDialog
          targetId={selectedTargetId}
          open={!!selectedTargetId}
          onOpenChange={(open) => !open && setSelectedTargetId(null)}
        />
      )}

      {/* CSV Validation Warning Dialog */}
      <AlertDialog open={!!csvValidationResult} onOpenChange={(open) => !open && handleCancelAddTargets()}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              CSV Validation Warnings
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                <p>The uploaded CSV has some differences from your campaign structure:</p>

                {csvValidationResult?.missingVariables && csvValidationResult.missingVariables.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h4 className="font-medium text-red-800 text-sm flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" />
                      Missing Variables
                    </h4>
                    <p className="text-xs text-red-700 mt-1">
                      These variables are used in your messages but not found in the CSV:
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {csvValidationResult.missingVariables.map(v => (
                        <Badge key={v} variant="destructive" className="text-xs">{`{${v}}`}</Badge>
                      ))}
                    </div>
                    <p className="text-xs text-red-600 mt-2 font-medium">
                      ⚠️ Messages will show the variable placeholder instead of personalized content.
                    </p>
                  </div>
                )}

                {csvValidationResult?.duplicateUrls && csvValidationResult.duplicateUrls.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <h4 className="font-medium text-amber-800 text-sm">Duplicate URLs Found</h4>
                    <p className="text-xs text-amber-700 mt-1">
                      {csvValidationResult.duplicateUrls.length} target(s) already exist in this campaign and will be skipped.
                    </p>
                  </div>
                )}

                {csvValidationResult?.extraFields && csvValidationResult.extraFields.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="font-medium text-blue-800 text-sm">New Fields Detected</h4>
                    <p className="text-xs text-blue-700 mt-1">
                      These fields are not used in your current messages: {csvValidationResult.extraFields.join(', ')}
                    </p>
                  </div>
                )}

                <div className="bg-muted rounded-lg p-3">
                  <p className="text-sm font-medium">
                    {pendingTargetsToAdd.length} target(s) will be added
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelAddTargets}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAddTargets}
              disabled={addTargetsMutation.isPending || pendingTargetsToAdd.length === 0}
            >
              {addTargetsMutation.isPending ? "Adding..." : `Add ${pendingTargetsToAdd.length} Targets Anyway`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* AI Knowledge Linker Dialog */}
      <Dialog open={knowledgeLinkerOpen} onOpenChange={setKnowledgeLinkerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>AI Knowledge Profiles</DialogTitle>
          </DialogHeader>
          <CampaignKnowledgeLinker 
            campaignId={id!} 
            workspaceId={workspace?.id || ""} 
          />
        </DialogContent>
      </Dialog>
      {capacityDialog}
    </div>

  );
}

// Memoized chart bodies — Recharts is expensive to lay out, so we isolate the
// two ResponsiveContainer subtrees behind React.memo. They re-render only when
// `data` (chartData) or `loading` (dailyStats === undefined) actually change,
// not when unrelated parent state (search input, page nav, dialog toggles)
// triggers a re-render of SimplifiedAnalyticsCharts.
type ChartDatum = {
  date: string;
  displayDate: string;
  connectionsSent: number;
  messagesSent: number;
  connectionsAccepted: number;
  repliesReceived: number;
};

const ConnectionPerformanceChart = memo(({ data, loading }: { data: ChartDatum[]; loading: boolean }) => {
  if (loading) {
    return (
      <div className="h-full w-full flex items-end gap-2 px-6 pb-6">
        {Array.from({ length: 14 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-muted/60 animate-pulse rounded-t"
            style={{ height: `${30 + ((i * 13) % 60)}%` }}
          />
        ))}
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorAccepted" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="displayDate" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} minTickGap={30} />
        <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
        <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
        <Legend />
        <Area type="monotone" dataKey="connectionsSent" name="Sent" stroke="#8884d8" fillOpacity={1} fill="url(#colorSent)" />
        <Area type="monotone" dataKey="connectionsAccepted" name="Accepted" stroke="#10b981" fillOpacity={1} fill="url(#colorAccepted)" />
      </AreaChart>
    </ResponsiveContainer>
  );
});
ConnectionPerformanceChart.displayName = "ConnectionPerformanceChart";

const EngagementVolumeChart = memo(({ data, loading }: { data: ChartDatum[]; loading: boolean }) => {
  if (loading) {
    return (
      <div className="h-full w-full flex items-end gap-2 px-6 pb-6">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-muted/60 animate-pulse rounded-t"
            style={{ height: `${25 + ((i * 17) % 65)}%` }}
          />
        ))}
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <XAxis dataKey="displayDate" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} minTickGap={30} />
        <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
        <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
        <Legend />
        <Bar dataKey="messagesSent" name="Messages" fill="#94a3b8" radius={[4, 4, 0, 0]} />
        <Bar dataKey="repliesReceived" name="Replies" fill="#3b82f6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
});
EngagementVolumeChart.displayName = "EngagementVolumeChart";

// Sub-component for Analytics Charts
function SimplifiedAnalyticsCharts({
  campaignId,
  stats,
  timeRange,
  setTimeRange,
  targets: allTargets,
  isDetailedView = false,
  activeTab,
  setActiveTab
}: {
  campaignId: string,
  stats: any,
  timeRange: string,
  setTimeRange: (range: string) => void,
  targets?: any[],
  isDetailedView?: boolean,
  activeTab: "analytics" | "details",
  setActiveTab: (tab: "analytics" | "details") => void
}) {
  const { workspace } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const ITEMS_PER_PAGE = 25;
  const queryClient = useQueryClient();

  // --- Target removal (client side) -------------------------------------
  // Only people the campaign has never contacted can be removed, so outreach
  // history and reporting are never erased.
  const [selectedTargets, setSelectedTargets] = useState<Set<string>>(new Set());
  const [removeTargetId, setRemoveTargetId] = useState<string | null>(null);
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);

  const canRemoveTarget = (t: any) =>
    !t?.connection_sent_at &&
    !t?.connection_accepted_at &&
    !t?.reply_received_at &&
    (t?.current_sequence_step || 0) < 1 &&
    ["pending", "visited"].includes(t?.status);

  const invalidateAfterRemoval = () => {
    queryClient.invalidateQueries({ queryKey: ["campaign-targets", campaignId] });
    queryClient.invalidateQueries({ queryKey: ["campaign-stats", campaignId] });
    queryClient.invalidateQueries({ queryKey: ["campaign-analytics-targets", campaignId] });
  };

  const removeTargetsMutation = useMutation({
    mutationFn: async (targetIds: string[]) => {
      const { error } = await supabase.from("targets").delete().in("id", targetIds);
      if (error) throw error;
      return targetIds.length;
    },
    onSuccess: (count) => {
      toast.success(count === 1 ? "Target removed" : `${count} targets removed`);
      invalidateAfterRemoval();
      setSelectedTargets(new Set());
      setRemoveTargetId(null);
      setBulkRemoveOpen(false);
    },
    onError: (error: any) => {
      toast.error("Could not remove targets: " + error.message);
    },
  });

  // URL-backed filter/search/page state so navigating away and back returns
  // to the exact same view. Search input is debounced (below) to avoid
  // history spam while typing.
  const targetFilter = searchParams.get('filter') || 'all';
  const setTargetFilter = (filter: string) => {
    const next = new URLSearchParams(searchParams);
    if (filter === 'all') next.delete('filter'); else next.set('filter', filter);
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const currentPage = Math.max(1, Number(searchParams.get('page')) || 1);
  const setCurrentPage = (updater: number | ((p: number) => number)) => {
    const nextPage = typeof updater === 'function' ? updater(currentPage) : updater;
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page'); else next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };

  // Local search state with debounced URL sync.
  const urlSearch = searchParams.get('q') || '';
  const [targetSearch, setTargetSearchLocal] = useState(urlSearch);
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (!targetSearch) next.delete('q'); else next.set('q', targetSearch);
      // Reset page when search changes
      if (targetSearch !== urlSearch) next.delete('page');
      setSearchParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSearch]);
  const setTargetSearch = setTargetSearchLocal;

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (timeRange) {
      case "week": return { start: subDays(now, 7), end: now };
      case "month": return { start: subDays(now, 30), end: now };
      case "lifetime": return { start: new Date(2020, 0, 1), end: now };
      default: return { start: subDays(now, 30), end: now };
    }
  }, [timeRange]);

  const { data: dailyStats } = useQuery({
    queryKey: ["campaign-analytics-daily", campaignId, timeRange],
    queryFn: async () => {
      let query = supabase
        .from("campaign_daily_stats")
        .select("stat_date, connections_sent_today, messages_sent_today")
        .eq("campaign_id", campaignId);

      if (timeRange !== 'lifetime') {
        query = query.gte("stat_date", dateRange.start.toISOString()).lte("stat_date", dateRange.end.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!campaignId,
    // Keep previous chart visible while switching time ranges → no blank flash.
    placeholderData: keepPreviousData,
  });

  const { data: targets } = useQuery({
    queryKey: ["campaign-analytics-targets", campaignId, timeRange],
    queryFn: async () => {
      let query = supabase
        .from("targets")
        .select("connection_accepted_at, reply_received_at, reply_intent")
        .eq("campaign_id", campaignId);

      if (timeRange !== 'lifetime') {
        query = query.or(`connection_accepted_at.gte.${dateRange.start.toISOString()},reply_received_at.gte.${dateRange.start.toISOString()}`);
      } else {
        query = query.or("connection_accepted_at.not.is.null,reply_received_at.not.is.null");
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
  });

  // Fetch intent breakdown for all replied targets (not time-filtered)
  const { data: intentCounts } = useQuery({
    queryKey: ["campaign-intent-breakdown", campaignId],
    queryFn: async () => {
      const results = { positive: 0, neutral: 0, negative: 0, unclassified: 0 };
      
      const { data, error } = await supabase
        .from("targets")
        .select("reply_intent")
        .eq("campaign_id", campaignId)
        .eq("status", "replied");

      if (error) throw error;
      
      (data || []).forEach((t: any) => {
        if (t.reply_intent === "positive") results.positive++;
        else if (t.reply_intent === "neutral") results.neutral++;
        else if (t.reply_intent === "negative") results.negative++;
        else results.unclassified++;
      });
      
      return results;
    },
    enabled: !!campaignId,
  });

  const chartData = useMemo(() => {
    if (!dailyStats || !targets) return [];

    // For lifetime, derive the date range from actual data instead of using fixed range
    let chartStart = dateRange.start;
    let chartEnd = dateRange.end;
    
    if (timeRange === 'lifetime' && dailyStats.length > 0) {
      const dates = dailyStats.map(s => new Date(s.stat_date));
      const targetDates = targets
        .flatMap(t => [t.connection_accepted_at, t.reply_received_at].filter(Boolean))
        .map(d => new Date(d!));
      const allDates = [...dates, ...targetDates];
      if (allDates.length > 0) {
        chartStart = new Date(Math.min(...allDates.map(d => d.getTime())));
        chartEnd = new Date(Math.max(...allDates.map(d => d.getTime())));
      }
    }

    const days = eachDayOfInterval({ start: chartStart, end: chartEnd });
    const dataMap = new Map();

    days.forEach(day => {
      const dateStr = format(day, "yyyy-MM-dd");
      dataMap.set(dateStr, {
        date: dateStr,
        displayDate: format(day, "MMM d"),
        connectionsSent: 0,
        messagesSent: 0,
        connectionsAccepted: 0,
        repliesReceived: 0,
      });
    });

    dailyStats.forEach(stat => {
      const entry = dataMap.get(stat.stat_date);
      if (entry) {
        entry.connectionsSent += (stat.connections_sent_today || 0);
        entry.messagesSent += (stat.messages_sent_today || 0);
      }
    });

    targets.forEach(target => {
      if (target.connection_accepted_at) {
        const dateStr = format(new Date(target.connection_accepted_at), "yyyy-MM-dd");
        if (dataMap.has(dateStr)) dataMap.get(dateStr).connectionsAccepted += 1;
      }
      if (target.reply_received_at) {
        const dateStr = format(new Date(target.reply_received_at), "yyyy-MM-dd");
        if (dataMap.has(dateStr)) dataMap.get(dateStr).repliesReceived += 1;
      }
    });

    return Array.from(dataMap.values());
  }, [dailyStats, targets, dateRange, timeRange]);

  // Lifetime counters (used for campaign completion progress, target totals, etc.)
  const lifetimeConnected = stats?.connectionAccepted || 0;
  const lifetimeReplied = stats?.replied || 0;
  const lifetimeSent = stats?.connectionSent || 0;

  const totals = useMemo(() => {
    return chartData.reduce((acc, curr) => ({
      sent: acc.sent + curr.connectionsSent,
      accepted: acc.accepted + curr.connectionsAccepted,
      replied: acc.replied + curr.repliesReceived,
      messages: acc.messages + curr.messagesSent
    }), { sent: 0, accepted: 0, replied: 0, messages: 0 });
  }, [chartData]);


  // Time-filtered KPI counters (respect Week / Month / Lifetime toggle)
  const sentCount = totals.sent;
  const connectedCount = totals.accepted;
  const repliedCount = totals.replied;
  const acceptanceRate = sentCount > 0 ? ((connectedCount / sentCount) * 100).toFixed(1) : "0";
  const replyRate = connectedCount > 0 ? ((repliedCount / connectedCount) * 100).toFixed(1) : "0";

  const timeRangeLabel = timeRange === 'week' ? 'this week' : timeRange === 'month' ? 'this month' : 'all time';


  // Filter targets for Details tab
  const filteredTargets = useMemo(() => {
    if (!allTargets) return [];
    let filtered = allTargets;
    if (targetFilter === "sent") {
      // Sent = connection_sent_at IS NOT NULL && connection_accepted_at IS NULL
      filtered = filtered.filter(t => t.connection_sent_at && !t.connection_accepted_at);
    } else if (targetFilter === "accepted") {
      filtered = filtered.filter(t => !!t.connection_accepted_at);
    } else if (targetFilter === "messaged_ts") {
      filtered = filtered.filter(t => (t.current_sequence_step || 0) >= 2);
    } else if (targetFilter !== "all") {
      filtered = filtered.filter(t => t.status === targetFilter);
    }
    if (targetSearch.trim()) {
      const search = targetSearch.toLowerCase();
      filtered = filtered.filter(t =>
        (t.name || "").toLowerCase().includes(search) ||
        (t.first_name || "").toLowerCase().includes(search) ||
        (t.last_name || "").toLowerCase().includes(search) ||
        (t.company || "").toLowerCase().includes(search) ||
        (t.title || "").toLowerCase().includes(search)
      );
    }
    return filtered;
  }, [allTargets, targetFilter, targetSearch]);

  const targetStatusCounts = useMemo(() => {
    if (!allTargets) return {};
    const counts: Record<string, number> = {};
    allTargets.forEach(t => {
      counts[t.status] = (counts[t.status] || 0) + 1;
      // Count "sent" separately using timestamp logic
      if (t.connection_sent_at && !t.connection_accepted_at) {
        counts["sent"] = (counts["sent"] || 0) + 1;
      }
      // Count "accepted" using timestamp (cumulative, not transient status)
      if (t.connection_accepted_at) {
        counts["accepted"] = (counts["accepted"] || 0) + 1;
      }
      // Count "messaged_ts" using sequence step (cumulative)
      if ((t.current_sequence_step || 0) >= 2) {
        counts["messaged_ts"] = (counts["messaged_ts"] || 0) + 1;
      }
    });
    return counts;
  }, [allTargets]);

  // Reset page when filters change
  const totalPages = Math.ceil(filteredTargets.length / ITEMS_PER_PAGE);
  const paginatedTargets = filteredTargets.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Never let a selection survive a view change — otherwise rows the user can
  // no longer see could be removed by the bulk action.
  useEffect(() => {
    setSelectedTargets(new Set());
  }, [targetFilter, currentPage, urlSearch]);

  const removableOnPage = paginatedTargets.filter(canRemoveTarget);
  const allPageSelected =
    removableOnPage.length > 0 && removableOnPage.every(t => selectedTargets.has(t.id));

  const togglePageSelection = () => {
    const next = new Set(selectedTargets);
    if (allPageSelected) removableOnPage.forEach(t => next.delete(t.id));
    else removableOnPage.forEach(t => next.add(t.id));
    setSelectedTargets(next);
  };

  const toggleTargetSelection = (targetId: string) => {
    const next = new Set(selectedTargets);
    if (next.has(targetId)) next.delete(targetId);
    else next.add(targetId);
    setSelectedTargets(next);
  };

  // Charts content (reused in both modes)
  const chartsContent = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="col-span-1 lg:col-span-2 shadow-sm border-sidebar-border/60">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Connection Performance</CardTitle>
            <CardDescription>Sent vs Accepted invitations</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="pl-0">
          <div className="h-[300px] w-full">
            <ConnectionPerformanceChart data={chartData} loading={dailyStats === undefined} />

          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-sidebar-border/60">
        <CardHeader>
          <CardTitle>Engagement Volume</CardTitle>
          <CardDescription>Messages vs Replies</CardDescription>
        </CardHeader>
        <CardContent className="pl-0">
          <div className="h-[250px] w-full">
            <EngagementVolumeChart data={chartData} loading={dailyStats === undefined} />

          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-sidebar-border/60">
        <CardHeader>
          <CardTitle>Campaign Funnel</CardTitle>
          <CardDescription>Overall Conversion</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Sent</span>
                <span className="text-muted-foreground">{sentCount}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-slate-400 w-full" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Accepted</span>
                <span className="text-muted-foreground">{connectedCount} ({acceptanceRate}%)</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${Math.min(100, parseFloat(acceptanceRate))}%` }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Replied</span>
                <span className="text-muted-foreground">{repliedCount} ({replyRate}%)</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-500"
                  style={{ width: `${Math.min(100, parseFloat(replyRate))}%` }}
                />
              </div>
              {intentCounts && (intentCounts.positive + intentCounts.neutral + intentCounts.negative) > 0 && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 pl-1">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    {intentCounts.positive} positive
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                    {intentCounts.neutral} neutral
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-destructive" />
                    {intentCounts.negative} negative
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Details tab content with pagination
  const detailsContent = (
    <Card className="shadow-sm border-sidebar-border/60">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <CardTitle className="text-base">Target Audience</CardTitle>
          <div className="flex items-center gap-2 ml-auto">
            {selectedTargets.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                className="h-9 text-xs"
                onClick={() => setBulkRemoveOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Remove selected ({selectedTargets.size})
              </Button>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search targets..."
                value={targetSearch}
                onChange={(e) => { setTargetSearch(e.target.value); setCurrentPage(1); }}
                className="pl-8 h-9 w-[200px] text-sm"
              />
            </div>
          </div>
        </div>
        {/* Status filter tabs */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {[
            { key: "all", label: "All", count: allTargets?.length || 0 },
            { key: "pending", label: "Pending", count: targetStatusCounts["pending"] || 0 },
            { key: "sent", label: "Sent", count: targetStatusCounts["sent"] || 0 },
            { key: "accepted", label: "Connected", count: targetStatusCounts["accepted"] || 0 },
            { key: "messaged_ts", label: "Messaged", count: targetStatusCounts["messaged_ts"] || 0 },
            { key: "replied", label: "Replied", count: targetStatusCounts["replied"] || 0 },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setTargetFilter(key)}
              className={cn(
                "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                targetFilter === key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              )}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 pl-6">
                  <Checkbox
                    checked={allPageSelected}
                    disabled={removableOnPage.length === 0}
                    onCheckedChange={togglePageSelection}
                    aria-label="Select all removable targets on this page"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Profile</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Step</TableHead>
                <TableHead className="w-12 text-right pr-6">Remove</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTargets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    {targetSearch ? "No targets match your search" : "No targets in this campaign"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTargets.map((target) => (
                  <TableRow
                    key={target.id}
                    className={cn("hover:bg-muted/50", selectedTargets.has(target.id) && "bg-primary/5")}
                  >
                    <TableCell className="pl-6">
                      <span title={canRemoveTarget(target) ? undefined : "Already contacted — cannot be removed"}>
                        <Checkbox
                          checked={selectedTargets.has(target.id)}
                          disabled={!canRemoveTarget(target)}
                          onCheckedChange={() => toggleTargetSelection(target.id)}
                          aria-label="Select target"
                        />
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {target.first_name && target.last_name
                        ? `${target.first_name} ${target.last_name}`
                        : target.name || "Unknown"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {target.profile_url ? (
                          <a
                            href={target.profile_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline whitespace-nowrap"
                          >
                            View Profile
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                        {target.profile_resolve_status === "valid" && (
                          <span title="Profile resolved">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          </span>
                        )}
                        {target.profile_resolve_status === "invalid" && (
                          <span title="Profile invalid">
                            <XCircle className="h-4 w-4 text-rose-500" />
                          </span>
                        )}
                        {(target.profile_resolve_status === "pending" || target.profile_resolve_status === "error" || !target.profile_resolve_status) && (
                          <span title="Profile not yet resolved">
                            <Minus className="h-4 w-4 text-slate-400" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                      {target.title || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">
                      {target.company || "—"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={target.status} className="text-xs" />
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {target.current_sequence_step || 0}
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      {canRemoveTarget(target) ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => setRemoveTargetId(target.id)}
                          aria-label="Remove target"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <span className="text-xs text-muted-foreground">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredTargets.length)} of {filteredTargets.length}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Single removal confirmation */}
        <AlertDialog open={!!removeTargetId} onOpenChange={(open) => !open && setRemoveTargetId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this target?</AlertDialogTitle>
              <AlertDialogDescription>
                They will be removed from this campaign and will not be contacted. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => removeTargetId && removeTargetsMutation.mutate([removeTargetId])}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={removeTargetsMutation.isPending}
              >
                {removeTargetsMutation.isPending ? "Removing..." : "Remove"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk removal confirmation */}
        <AlertDialog open={bulkRemoveOpen} onOpenChange={setBulkRemoveOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {selectedTargets.size} target{selectedTargets.size > 1 ? "s" : ""}?</AlertDialogTitle>
              <AlertDialogDescription>
                They will be removed from this campaign and will not be contacted. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => removeTargetsMutation.mutate(Array.from(selectedTargets))}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={removeTargetsMutation.isPending}
              >
                {removeTargetsMutation.isPending ? "Removing..." : `Remove ${selectedTargets.size}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Overview Cards — always visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Connections Sent</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.sent.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">In selected period</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acceptance Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{acceptanceRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">{totals.accepted.toLocaleString()} accepted · {timeRangeLabel}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Reply Rate</CardTitle>
            <MessageSquare className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{replyRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">{totals.replied.toLocaleString()} replies · {timeRangeLabel}</p>
            {intentCounts && (intentCounts.positive + intentCounts.neutral + intentCounts.negative) > 0 && (
              <div className="flex items-center gap-1.5 mt-2">
                <Badge variant="success" className="text-[10px] px-1.5 py-0 h-4">{intentCounts.positive} 👍</Badge>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">{intentCounts.neutral} —</Badge>
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">{intentCounts.negative} 👎</Badge>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.messages.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">Follow-ups & replies</p>
          </CardContent>
        </Card>
      </div>

      {/* Results Intent Card — always visible */}
      <ResultsIntentCard campaignId={campaignId} />

      {/* Conditional: Tabs (detailed view) vs Charts only (simplified view) */}
      {isDetailedView ? (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "analytics" | "details")} className="w-full">
          <TabsList className="grid w-full max-w-xs grid-cols-2">
            <TabsTrigger value="analytics" className="gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="details" className="gap-1.5">
              <Target className="h-3.5 w-3.5" />
              Details
            </TabsTrigger>
          </TabsList>
          {/* forceMount keeps both tab panels in the DOM so switching is
              instant and chart layout state is preserved between toggles. */}
          <TabsContent
            value="analytics"
            forceMount
            className={cn("mt-6", activeTab !== "analytics" && "hidden")}
          >
            {chartsContent}
          </TabsContent>
          <TabsContent
            value="details"
            forceMount
            className={cn("mt-6", activeTab !== "details" && "hidden")}
          >
            {detailsContent}
          </TabsContent>
        </Tabs>
      ) : (
        chartsContent
      )}
    </div>
  );
}
